import {
  AddressModal,
  ColumnsType,
  DeleteIcon,
  EditIcon,
  InfiniteTable,
} from '@/components/_common';
import { AddressDefaultType } from '@/components/_common/AddressModal/types';
import SkeletonCustom from '@/components/_common/SkeletonCustom';
import {
  useCreateCustomerAddress,
  useCustomerAddresses,
  useDeleteCustomerAddress,
  useUpdateCustomerAddress,
} from '@/core/domain/people/CustomerAddresses/CustomerAddresses.hooks';
import {
  CreateCustomerAddressRequest,
  CustomerAddress,
  UpdateCustomerAddressRequest,
} from '@/core/domain/people/CustomerAddresses/CustomerAddresses.types';
import { useDisclosure } from '@/hooks';
import { useAddressColumns } from '@/hooks/useAddressColumns';
import { isAppPortocar } from '@/index';
import { Button, Collapse, Divider, message, Modal, Space } from 'antd';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';

interface Props {
  customerId: number;
}

const CustomerAddressCollapse: React.FC<Props> = ({ customerId }) => {
  const intl = useIntl();
  const { columns: columnsAddress } = useAddressColumns<CustomerAddress>();
  const [selectedAddress, setSelectedAddress] = useState<CustomerAddress>();
  const [modalVisible, setModalVisible] = useDisclosure();

  const addressesContacts = useCustomerAddresses(customerId);
  const createAddressMutation = useCreateCustomerAddress(customerId);
  const updateAddressMutation = useUpdateCustomerAddress(customerId);
  const deleteAddressMutation = useDeleteCustomerAddress(customerId);

  const addresses = addressesContacts.data?.data || [];

  const handleModalVisible = async (address?: CustomerAddress) => {
    setSelectedAddress(!!address ? address : undefined);
    setModalVisible.toggle();
  };

  const handleModalOkClick = async (values: AddressDefaultType) => {
    if (values?.id) {
      const obj: UpdateCustomerAddressRequest = {
        ...values,
        main: !!values.main,
        delivery: !!values.delivery,
      };

      await updateAddressMutation.mutateAsync(obj);
    } else {
      const obj: CreateCustomerAddressRequest = {
        ...values,
        main: !!values.main,
        delivery: !!values.delivery,
      };

      await createAddressMutation.mutateAsync(obj);
    }
  };

  const handleRemoveClick = (address: CustomerAddress) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'customers.address.remove',
      }),
      content: intl.formatMessage({
        id: 'customers.address.remove.alert',
      }),
      onOk: async () => {
        if (addresses.length === 1 && isAppPortocar) {
          message.destroy();
          message.warning(
            'O cadastro requer pelo menos um endereço obrigatório.',
          );
        } else {
          await deleteAddressMutation.mutateAsync(address.id);
        }
      },
      okType: 'danger',
    });
  };

  const columns: ColumnsType<CustomerAddress> = [
    ...columnsAddress,
    {
      key: 'action',
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      align: 'center',
      fixed: 'right',
      width: 80,
      render: (_, row) => (
        <Space>
          <EditIcon iconProps={{ onClick: () => handleModalVisible(row) }} />

          <Divider type="vertical" />

          <DeleteIcon onClick={() => handleRemoveClick(row)} />
        </Space>
      ),
    },
  ];

  if (!customerId) {
    return null;
  }

  return (
    <SkeletonCustom
      loading={addressesContacts.isLoading}
      skeletonType="INPUT"
      skeletonCount={1}
      style={{ marginBottom: 12 }}
    >
      <Collapse
        accordion
        className="contact-collapse"
        defaultActiveKey={!!addresses.length ? ['1'] : undefined}
      >
        <Collapse.Panel
          header="Endereços"
          extra={
            !!customerId && (
              <Button
                shape="round"
                block
                type="primary"
                onClick={(event) => {
                  if (!!addresses.length) {
                    event.stopPropagation();
                  }

                  handleModalVisible();
                }}
              >
                Adicionar
              </Button>
            )
          }
          key="1"
        >
          {!!customerId && (
            <>
              <InfiniteTable
                dataSource={addresses}
                rowKey={(row) => row.id}
                columns={columns}
                loading={addressesContacts.isFetching}
                pagination={false}
              />

              <AddressModal
                visible={modalVisible}
                onClose={handleModalVisible}
                onOk={handleModalOkClick}
                address={selectedAddress}
                loading={
                  createAddressMutation.isLoading ||
                  updateAddressMutation.isLoading
                }
                hasAddress={!!addressesContacts.data?.data.length}
                removeDelivery={false}
                removeDescription={false}
                removeMain={false}
              />
            </>
          )}
        </Collapse.Panel>
      </Collapse>
    </SkeletonCustom>
  );
};

export default CustomerAddressCollapse;
